import { apiVpnStar } from "../api/apiVpnStar";
import {
  GEO_COUNTRY_STORAGE_KEY,
  isAppLocale,
  LOCALE_STORAGE_KEY,
  LOCALE_USER_CHOSEN_KEY,
  SITE_DEFAULT_LOCALE_STORAGE_KEY,
} from "./locales";
import type { AppLocale } from "./types";

export function readStoredLocale(): AppLocale | null {
  const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
  return isAppLocale(raw) ? raw : null;
}

export function readUserChosenLocale(): AppLocale | null {
  if (localStorage.getItem(LOCALE_USER_CHOSEN_KEY) !== "1") {
    return null;
  }
  return readStoredLocale();
}

export function persistUserLocale(locale: AppLocale): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  localStorage.setItem(LOCALE_USER_CHOSEN_KEY, "1");
}

export function persistAutoLocale(locale: AppLocale): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  localStorage.removeItem(LOCALE_USER_CHOSEN_KEY);
}

export function readCachedCountryCode(): string | null {
  const raw = localStorage
    .getItem(GEO_COUNTRY_STORAGE_KEY)
    ?.trim()
    .toUpperCase();
  return raw && raw.length === 2 ? raw : null;
}

export function cacheDetectedCountryCode(countryCode: string): void {
  const normalized = countryCode.trim().toUpperCase();
  if (normalized.length === 2) {
    localStorage.setItem(GEO_COUNTRY_STORAGE_KEY, normalized);
  }
}

export function readCachedOperatorDefaultLocale(): AppLocale | null {
  const raw = localStorage.getItem(SITE_DEFAULT_LOCALE_STORAGE_KEY);
  return isAppLocale(raw) ? raw : null;
}

export function cacheOperatorDefaultLocale(locale: AppLocale): void {
  localStorage.setItem(SITE_DEFAULT_LOCALE_STORAGE_KEY, locale);
}

export function readLocaleFromUrl(): AppLocale | null {
  const param = new URLSearchParams(window.location.search).get("lang");
  return isAppLocale(param) ? param : null;
}

/** Sync resolution for tests and LocaleProvider fallback; prefer resolveLocaleBeforeFirstPaint at boot. */
export function resolveInitialLocale(): AppLocale {
  const fromUrl = readLocaleFromUrl();
  if (fromUrl) {
    persistUserLocale(fromUrl);
    return fromUrl;
  }

  const userChosen = readUserChosenLocale();
  if (userChosen) {
    return userChosen;
  }

  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }

  const cachedCountry = readCachedCountryCode();
  if (cachedCountry) {
    const cachedDefault = readCachedOperatorDefaultLocale() ?? "ru";
    const resolved = localeFromCountryCode(cachedCountry) ?? cachedDefault;
    persistAutoLocale(resolved);
    return resolved;
  }

  return readCachedOperatorDefaultLocale() ?? "ru";
}

/** Geo beats the operator default; used with /api/site-config/ on first visit. */
export function resolveAutomaticLocale(
  countryCode: string | null,
  siteDefault: AppLocale,
): AppLocale {
  return localeFromCountryCode(countryCode) ?? siteDefault;
}

export function shouldRunGeoLocaleDetection(): boolean {
  return !readStoredLocale() && !readLocaleFromUrl();
}

/**
 * Picks the locale once before React mounts so the first paint matches admin default / geo.
 * Returns immediately when the visitor already has a saved or URL locale.
 */
export async function resolveLocaleBeforeFirstPaint(): Promise<AppLocale> {
  const fromUrl = readLocaleFromUrl();
  if (fromUrl) {
    persistUserLocale(fromUrl);
    return fromUrl;
  }

  const userChosen = readUserChosenLocale();
  if (userChosen) {
    return userChosen;
  }

  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }

  const cachedCountry = readCachedCountryCode();
  if (cachedCountry) {
    const cachedDefault = readCachedOperatorDefaultLocale() ?? "ru";
    const resolved = localeFromCountryCode(cachedCountry) ?? cachedDefault;
    persistAutoLocale(resolved);
    return resolved;
  }

  const [siteConfig, countryCode] = await Promise.all([
    apiVpnStar.getSiteConfig().catch(() => null),
    detectCountryCode(),
  ]);
  const configuredDefault = isAppLocale(siteConfig?.default_locale)
    ? siteConfig.default_locale
    : "ru";
  cacheOperatorDefaultLocale(configuredDefault);
  if (countryCode) {
    cacheDetectedCountryCode(countryCode);
  }
  const resolved = resolveAutomaticLocale(countryCode, configuredDefault);
  persistAutoLocale(resolved);
  return resolved;
}

export async function detectCountryCode(): Promise<string | null> {
  try {
    const init: RequestInit = {};
    if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
      init.signal = AbortSignal.timeout(4000);
    }
    const response = await fetch("https://ipapi.co/country_code/", init);
    if (!response.ok) {
      return null;
    }
    const text = (await response.text()).trim();
    return text.length === 2 ? text.toUpperCase() : null;
  } catch {
    return null;
  }
}

export function localeFromCountryCode(
  countryCode: string | null,
): AppLocale | null {
  if (countryCode === "AZ") {
    return "az";
  }
  return null;
}
